/**
 * CardFlip — 카드 한 장을 탭으로 돌려 보는 3D 회전.
 *
 * 카드 실물을 손에 쥐면 뒤집어 본다. 스캔·시세 화면의 카드는 지금까지 '그림
 * 한 장'이었는데, 돌아가는 순간 **물건**이 된다. 공유 화면처럼 카드가 주인공인
 * 자리에서만 쓴다 — 목록·썸네일에서는 탭이 '열기'를 뜻하므로 쓰지 않는다.
 *
 * ── 어떻게 도는가 ─────────────────────────────────────────────────────
 * 탭하면 **180° 돌아 뒷면을 보이고, 잠깐 머물렀다, 왔던 길 그대로 되짚어
 * 앞면으로 돌아온다.** 손으로 카드를 들춰 봤다 놓는 동작이다.
 *
 * 뒤집힌 채 머무르지 않는 것이 요점이다 — 머무르면 '뒷면 상태'라는 게 생겨
 * 되돌릴 책임이 사용자에게 남는다. 여기서 하려던 건 상태 전환이 아니라
 * **한 번 들춰 보는 손짓**이다.
 *
 * 회전은 CSS 가 전부 한다. React 는 **지금 나가는 중인가 돌아오는 중인가**
 * 만 들고, 나머지(앞뒤 교체·거울 반전 방지)는 backface-visibility 가 90°에서
 * 정확히 갈라 준다. 각도는 transition 이라 중간에 탭해도 **지금 있는 자리에서
 * 이어서** 방향만 바꾼다 — 다시 시작하지 않으므로 연타해도 튀지 않는다.
 *
 * ⚠ 두 이미지는 처음부터 DOM 에 함께 있다(뒷면은 backface-visibility 로 숨을
 *   뿐 로드·디코드는 끝나 있다). 첫 탭에서 깜빡이지 않는 이유다.
 *
 * ⚠ 인라인으로 내려보내는 건 **JS 만 아는 값** 둘뿐이다 — 어디까지 돌았나
 *   (--cf-turn), 어디를 눌렀나(--cf-tilt-*). 나머지(그림자·광택·눌림)는
 *   CSS 가 각도에서 직접 계산한다. 그래서 시간축이 어긋날 자리가 없다.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CardArt from "./CardArt";
import styles from "./CardFlip.module.css";
import cardBackSrc from "../../assets/dev-cards/card-back.webp";

/* 아래 셋은 CSS(--flip-ms)와 짝이 맞아야 한다.
   나가는 길과 돌아오는 길을 같은 시간으로 두어 '왔던 길 그대로'가 눈에 보이게
   한다. 가운데 멈춤은 뒷면을 읽을 짧은 틈이다 — 없으면 스쳐 지나가 버린다. */
const OUT_MS = 380;
const HOLD_MS = 120;
const BACK_MS = 380;

/** 탭 지점이 만드는 기울기 상한. 4° 넘으면 '기울었다'가 아니라 '흔들린다'로 읽힌다 */
const TILT_MAX = 4;

/** idle = 앞면에 서 있음 · out = 뒷면으로 가는 중 · back = 앞면으로 되짚는 중 */
type Phase = "idle" | "out" | "back";

export interface CardFlipProps {
  /** 더미 카탈로그 전용 색 키. 실 카탈로그에는 없어 옵셔널이다 — 없으면 중립 배경 */
  colorKey?: string;
  /** 앞면 아트워크. 없으면 colorKey 그라디언트가 앞면이 된다 */
  imgUrl?: string;
  /** 뒷면. 카드마다 다르지 않아 기본값(공통 뒷면)을 쓴다 */
  backUrl?: string;
  /** 크기·비율은 호출부가 정한다 (CardArt 와 같은 규칙) */
  className?: string;
  /** 스크린리더용 카드 이름 */
  cardName: string;
}

export default function CardFlip({
  colorKey,
  imgUrl,
  backUrl = cardBackSrc,
  className,
  cardName,
}: CardFlipProps) {
  const { t } = useTranslation("card");
  /** 목표 각도 — 1 이면 180°(뒷면), 0 이면 0°(앞면). 실제 각도는 CSS 가 잇는다 */
  const [turn, setTurn] = useState(0);
  const [phase, setPhase] = useState<Phase>("idle");
  const [pressed, setPressed] = useState(false);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const timers = useRef<number[]>([]);

  const clearTimers = useCallback(() => {
    timers.current.forEach(window.clearTimeout);
    timers.current = [];
  }, []);
  useEffect(() => clearTimers, [clearTimers]);

  /** 누른 지점 → 기울기. 가운데를 누르면 0, 모서리로 갈수록 TILT_MAX 에 가까워진다 */
  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const nx = (e.clientX - (r.left + r.width / 2)) / (r.width / 2);
    const ny = (e.clientY - (r.top + r.height / 2)) / (r.height / 2);
    const clamp = (v: number) => Math.max(-1, Math.min(1, v)) * TILT_MAX;
    /* 위를 누르면 위가 뒤로 눕는다 → x 축 회전은 부호를 뒤집는다 */
    setTilt({ x: -clamp(ny), y: clamp(nx) });
    setPressed(true);
  }, []);

  const release = useCallback(() => setPressed(false), []);

  const handleClick = useCallback(() => {
    /* 눌림은 여기서 푼다 — pointerup 에서 풀면 크기가 먼저 돌아와 버려
       '회전과 함께 원래 크기로'가 안 된다 */
    setPressed(false);
    setTurn(1);
    setPhase("out");

    /* 돌아오는 중에 다시 탭하면 타이머를 새로 깐다 — 지금 있는 각도에서
       방향만 바꿔 다시 나간다. */
    clearTimers();
    timers.current.push(
      window.setTimeout(() => {
        setTurn(0);
        setPhase("back");
      }, OUT_MS + HOLD_MS),
      window.setTimeout(() => {
        setPhase("idle");
        setTilt({ x: 0, y: 0 }); // 다 돌고 나면 반듯하게 선다
      }, OUT_MS + HOLD_MS + BACK_MS),
    );
  }, [clearTimers]);

  return (
    <button
      type="button"
      className={`${styles.root}${className ? ` ${className}` : ""}`}
      data-pressed={pressed}
      /* 나가는 길과 돌아오는 길의 이징이 다르다 (CSS 참조) */
      data-phase={phase}
      style={{
        "--cf-turn": turn,
        "--cf-tilt-x": `${tilt.x}deg`,
        "--cf-tilt-y": `${tilt.y}deg`,
      } as React.CSSProperties}
      onPointerDown={handlePointerDown}
      onPointerCancel={release}
      onPointerLeave={release}
      onClick={handleClick}
      /* aria-pressed 를 달지 않는다 — 눌린 채 남는 상태가 없다(늘 앞면으로
         돌아온다). 버튼이 하는 일은 '한 번 들춰 본다' 하나다. */
      aria-label={t("flip.spin", { name: cardName })}
    >
      {/* 그림자는 회전하지 않는다 — 카드와 함께 돌면 바닥이 같이 기운다.
          정면일 때 넓고 부드럽게, 옆면에 가까울수록 좁고 옅게 (CSS 참조) */}
      <span className={styles.shadow} aria-hidden />

      <span className={styles.flipper}>
        <span className={`${styles.face} ${styles.front}`}>
          <CardArt className={styles.faceArt} colorKey={colorKey} imgUrl={imgUrl} />
        </span>
        <span className={`${styles.face} ${styles.back}`}>
          {/* eager — 첫 탭에서 뒷면을 기다리지 않게 미리 받아 둔다 */}
          <img className={styles.faceArt} src={backUrl} alt="" loading="eager" decoding="async" />
        </span>
      </span>
    </button>
  );
}
